import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { MessageSquareOff, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Badge, Button, Dialog, EmptyState, Spinner, toast } from '@nokowazzi/ui';
import { Table, Td, Th, Thead, Tr } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { Can } from '../features/admin/Can';
import { usePermissions } from '../features/admin/use-permissions';
import type { AdminReviewListItem, AdminReviewListResponse } from '../features/reviews/queries';
import { RatingStars } from '../features/reviews/RatingStars';
import { ApiError, apiFetch } from '../lib/api';
import { formatDateTime } from '../lib/datetime';

export const Route = createFileRoute('/_admin/reviews')({
  component: ReviewsScreen,
});

function ReviewsScreen() {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const [deleteTarget, setDeleteTarget] = useState<AdminReviewListItem | null>(null);

  const listQuery = useInfiniteQuery({
    queryKey: ['admin', 'reviews', 'list'] as const,
    queryFn: ({ pageParam }) =>
      apiFetch<AdminReviewListResponse>('/v1/admin/reviews', {
        query: { cursor: pageParam ?? undefined },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const deleteMutation = useMutation({
    mutationFn: (reviewId: string) =>
      apiFetch<unknown>(`/v1/admin/reviews/${reviewId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
      toast.show({ type: 'success', message: '리뷰를 삭제했습니다' });
      setDeleteTarget(null);
    },
    onError: (error) => {
      toast.show({
        type: 'error',
        message: error instanceof ApiError ? error.message : '삭제에 실패했습니다',
      });
    },
  });

  const items: AdminReviewListItem[] = listQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const canModerate = can('reviews:moderate');

  return (
    <>
      <PageHeader title="리뷰 관리" description="부적절 리뷰 모니터링 및 삭제" />

      {listQuery.isPending ? (
        <div className="flex min-h-52 items-center justify-center">
          <Spinner label="리뷰 목록 불러오는 중" />
        </div>
      ) : listQuery.isError ? (
        <EmptyState
          title="리뷰 목록을 불러오지 못했습니다"
          description={
            listQuery.error instanceof ApiError ? listQuery.error.message : '잠시 후 다시 시도해 주세요'
          }
          action={
            <Button variant="tertiary" size="sm" onClick={() => void listQuery.refetch()}>
              다시 시도
            </Button>
          }
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<MessageSquareOff size={40} strokeWidth={1.5} />}
          title="리뷰가 없습니다"
          description="등록된 리뷰가 없습니다"
        />
      ) : (
        <>
          <Table>
            <Thead>
              <tr>
                <Th className="w-28">별점</Th>
                <Th>내용</Th>
                <Th className="w-32">작성자</Th>
                <Th className="w-32">대상</Th>
                <Th className="w-36">일시</Th>
                {canModerate && (
                  <Th className="w-20 text-right">
                    <span className="sr-only">액션</span>
                  </Th>
                )}
              </tr>
            </Thead>
            <tbody>
              {items.map((review) => (
                <Tr key={review.id}>
                  <Td>
                    <RatingStars rating={review.rating} />
                  </Td>
                  <Td>
                    {review.comment != null && review.comment !== '' ? (
                      <span className="line-clamp-2 text-fg-secondary">{review.comment}</span>
                    ) : (
                      <span className="text-fg-tertiary">(내용 없음)</span>
                    )}
                    {review.tags.length > 0 && (
                      <span className="mt-1 flex flex-wrap gap-1">
                        {review.tags.map((tag) => (
                          <Badge key={tag} tone="neutral">
                            {tag}
                          </Badge>
                        ))}
                      </span>
                    )}
                  </Td>
                  <Td className="text-fg-secondary">{review.reviewer?.display_name ?? '—'}</Td>
                  <Td className="text-fg-secondary">{review.reviewee?.display_name ?? '—'}</Td>
                  <Td className="whitespace-nowrap text-caption2 text-fg-tertiary">
                    {formatDateTime(review.created_at)}
                  </Td>
                  {canModerate && (
                    <Td className="text-right">
                      <Can perm="reviews:moderate">
                        <button
                          type="button"
                          aria-label="리뷰 삭제"
                          onClick={() => setDeleteTarget(review)}
                          className="inline-flex size-8 items-center justify-center rounded-md text-danger-600 transition-colors hover:bg-danger-50"
                        >
                          <Trash2 className="size-4" aria-hidden="true" />
                        </button>
                      </Can>
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>

          {listQuery.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="tertiary"
                size="sm"
                loading={listQuery.isFetchingNextPage}
                onClick={() => void listQuery.fetchNextPage()}
              >
                더 보기
              </Button>
            </div>
          )}
        </>
      )}

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="리뷰 삭제"
        description="이 리뷰를 삭제합니다. 삭제는 감사 로그에 기록되며 되돌릴 수 없습니다."
        footer={
          <>
            <Button variant="tertiary" fullWidth onClick={() => setDeleteTarget(null)}>
              취소
            </Button>
            <Button
              variant="danger"
              fullWidth
              loading={deleteMutation.isPending}
              onClick={() => {
                if (deleteTarget != null) deleteMutation.mutate(deleteTarget.id);
              }}
            >
              삭제
            </Button>
          </>
        }
      >
        {deleteTarget != null && (
          <div className="rounded-md bg-subtle p-3">
            <RatingStars rating={deleteTarget.rating} />
            <p className="mt-1 text-caption1 text-fg-secondary">
              {deleteTarget.comment ?? '(내용 없음)'}
            </p>
          </div>
        )}
      </Dialog>
    </>
  );
}
